import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Flame, LogOut, Loader2, Star, EyeOff, Trash2, Check } from "lucide-react";

const TABS = ["Overview", "Bookings", "Calendar", "Enquiries", "Words", "Partners", "Gift Codes", "Merch", "The List"];

const CalendarTab = ({ data, token, reload }) => {
  const [blockDate, setBlockDate] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const block = async () => {
    if (!blockDate) return;
    await api.post("/admin/calendar/block", { date: blockDate }, { headers });
    setBlockDate("");
    toast.success("Day blocked. Yours again.");
    reload();
  };

  const unblock = async (d) => {
    await api.delete(`/admin/calendar/block/${d}`, { headers });
    reload();
  };

  const toggleSlot = async (slot) => {
    const next = data.active_slots.includes(slot)
      ? data.active_slots.filter((s) => s !== slot)
      : [...data.active_slots, slot].sort();
    try {
      await api.put("/admin/calendar/slots", { slots: next }, { headers });
      reload();
    } catch {
      toast.error("At least one slot must stay open.");
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="admin-calendar">
      <div className="border border-hearth-line bg-hearth-surface p-8">
        <h3 className="font-display font-bold text-2xl text-hearth-ink">Block a day</h3>
        <p className="text-hearth-muted text-sm mt-2 mb-6">The paddock, the grandkids, the sabbatical you still owe yourself. Blocked days show no slots at all.</p>
        <div className="flex gap-3">
          <input type="date" data-testid="calendar-block-date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)}
            min={new Date().toISOString().split("T")[0]}
            className="bg-hearth-bg border border-hearth-line px-4 py-3 text-sm text-hearth-ink focus:outline-none focus:border-hearth-ochre [color-scheme:dark]" />
          <button onClick={block} data-testid="calendar-block-button"
            className="bg-hearth-ember hover:bg-hearth-ochre hover:text-hearth-bg text-hearth-ink text-xs font-semibold uppercase tracking-widest px-6 transition-colors">
            Block It
          </button>
        </div>
        <div className="mt-6 space-y-2">
          {data.blocked_days.map((d) => (
            <div key={d} className="flex items-center justify-between border border-hearth-line px-4 py-3" data-testid={`blocked-day-${d}`}>
              <span className="text-hearth-ink text-sm">{d}</span>
              <button onClick={() => unblock(d)} data-testid={`unblock-${d}`} className="text-hearth-muted hover:text-hearth-ember text-xs uppercase tracking-widest transition-colors">Open it</button>
            </div>
          ))}
          {data.blocked_days.length === 0 && <p className="text-hearth-muted font-essay italic text-lg">Every day is open.</p>}
        </div>
      </div>
      <div className="border border-hearth-line bg-hearth-surface p-8">
        <h3 className="font-display font-bold text-2xl text-hearth-ink">Your hours</h3>
        <p className="text-hearth-muted text-sm mt-2 mb-6">Tick the slots you're willing to sit. Unticked ones vanish from the booking page.</p>
        <div className="space-y-3">
          {data.all_slots.map((s) => (
            <button key={s} onClick={() => toggleSlot(s)} data-testid={`slot-toggle-${s.replace(":", "")}`}
              className={`w-full flex items-center justify-between border px-5 py-4 text-sm transition-colors ${data.active_slots.includes(s) ? "border-hearth-ochre text-hearth-ink" : "border-hearth-line text-hearth-muted line-through"}`}>
              <span>{s}</span>
              <span className="text-xs uppercase tracking-widest">{data.active_slots.includes(s) ? "Open" : "Closed"}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("nobc_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("Overview");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const authed = (path) => api.get(path, { headers: { Authorization: `Bearer ${token}` } });

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("nobc_admin_token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    }
  };

  const load = async (t) => {
    if (!token) return;
    setLoading(true);
    const paths = {
      Overview: "/admin/overview", Bookings: "/admin/bookings", Enquiries: "/admin/enquiries",
      Words: "/admin/testimonials", Partners: "/admin/partners", "Gift Codes": "/admin/gifts", "The List": "/admin/subscribers",
      Merch: "/admin/merch-orders", Calendar: "/admin/calendar",
    };
    try {
      const res = await authed(paths[t]);
      setData((d) => ({ ...d, [t]: res.data }));
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("nobc_admin_token");
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(tab); }, [token, tab]);

  const patch = async (path, body, reloadTab) => {
    try {
      await api.patch(path, body, { headers: { Authorization: `Bearer ${token}` } });
      load(reloadTab);
    } catch { toast.error("That didn't stick."); }
  };

  const del = async (path, reloadTab) => {
    try {
      await api.delete(path, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Gone.");
      load(reloadTab);
    } catch { toast.error("That didn't work."); }
  };

  if (!token) {
    return (
      <div className="bg-hearth-bg min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-md border border-hearth-line bg-hearth-surface p-10" data-testid="admin-login-form">
          <Flame className="w-8 h-8 text-hearth-ember mb-6" />
          <h1 className="font-display font-bold text-3xl text-hearth-ink">Matt's door.</h1>
          <p className="text-hearth-muted text-sm mt-2 mb-8">No one else's.</p>
          <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
            className="w-full bg-hearth-bg border border-hearth-line px-5 py-4 text-sm text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre mb-4" />
          <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            className="w-full bg-hearth-bg border border-hearth-line px-5 py-4 text-sm text-hearth-ink placeholder:text-hearth-muted focus:outline-none focus:border-hearth-ochre mb-4" />
          {error && <p data-testid="admin-login-error" className="text-hearth-ember text-sm mb-4">{error}</p>}
          <button data-testid="admin-login-button" className="w-full bg-hearth-ember hover:bg-hearth-ochre hover:text-hearth-bg text-hearth-ink text-xs font-semibold uppercase tracking-widest py-4 transition-colors">
            Open the Door
          </button>
        </form>
      </div>
    );
  }

  const overview = data.Overview;

  return (
    <div className="bg-hearth-bg min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-hearth-line bg-hearth-surface">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Flame className="w-5 h-5 text-hearth-ember" />
            <span className="font-display font-bold text-hearth-ink">The Dashboard</span>
          </div>
          <button data-testid="admin-logout-button" onClick={() => { localStorage.removeItem("nobc_admin_token"); setToken(null); }}
            className="inline-flex items-center gap-2 text-hearth-muted hover:text-hearth-ink text-xs uppercase tracking-widest transition-colors">
            <LogOut className="w-4 h-4" /> Lock Up
          </button>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-px bg-hearth-line border border-hearth-line mb-10" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t} data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setTab(t)}
              className={`px-5 py-3 text-xs uppercase tracking-widest font-semibold transition-colors ${tab === t ? "bg-hearth-ember text-hearth-ink" : "bg-hearth-surface text-hearth-muted hover:text-hearth-ink"}`}>
              {t}
            </button>
          ))}
        </div>
        {loading && <p className="text-hearth-muted flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}

        {!loading && tab === "Overview" && overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-hearth-line border border-hearth-line" data-testid="admin-overview">
            {Object.entries({ "Confirmed sessions": overview.confirmed, "Awaiting payment": overview.awaiting_payment, "Open enquiries": overview.enquiries_open, "Words on the wall": overview.testimonials, "Partner hands up": overview.partners, "Active gift codes": overview.gifts_active, "Relaunch list": overview.subscribers, "Total bookings": overview.bookings }).map(([k, v]) => (
              <div key={k} className="bg-hearth-surface p-8">
                <p className="font-display font-bold text-4xl text-hearth-ink">{v}</p>
                <p className="text-hearth-muted text-xs uppercase tracking-widest mt-2">{k}</p>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "Bookings" && (
          <div className="space-y-4" data-testid="admin-bookings">
            {(data.Bookings || []).map((b) => (
              <div key={b.id} className="border border-hearth-line bg-hearth-surface p-6 grid md:grid-cols-4 gap-4 text-sm" data-testid={`admin-booking-${b.id}`}>
                <div><p className="text-hearth-ink font-semibold">{b.name}</p><p className="text-hearth-muted">{b.email}</p>{b.phone && <p className="text-hearth-muted">{b.phone}</p>}</div>
                <div><p className="text-hearth-ochre uppercase text-xs tracking-widest">{b.session_type.replace(/_/g, " ")}</p><p className="text-hearth-muted">{b.setting.replace(/_/g, " ")} · {b.preferred_date} {b.time_slot}</p>
                  <p className="text-hearth-muted">{b.karen_present ? "Karen in · " : ""}{b.companion ? "Bringing someone" : ""}</p></div>
                <div><p className={b.status === "confirmed" ? "text-hearth-ochre" : "text-hearth-muted"}>{b.status.replace(/_/g, " ")} · {b.payment_status}</p>
                  {b.skill_level && <p className="text-hearth-muted">Skill: {b.skill_level}{b.qualified_to_lead === true ? " (qualified to lead)" : b.qualified_to_lead === false ? " (Matt leads)" : ""}</p>}</div>
                <div>{b.notes && <p className="text-hearth-muted font-essay italic">"{b.notes}"</p>}</div>
              </div>
            ))}
            {(data.Bookings || []).length === 0 && <p className="text-hearth-muted font-essay italic text-xl">No bookings yet.</p>}
          </div>
        )}

        {!loading && tab === "Calendar" && data.Calendar && (
          <CalendarTab data={data.Calendar} token={token} reload={() => load("Calendar")} />
        )}

        {!loading && tab === "Enquiries" && (
          <div className="space-y-4" data-testid="admin-enquiries">
            {(data.Enquiries || []).map((q) => (
              <div key={q.id} className="border border-hearth-line bg-hearth-surface p-6" data-testid={`admin-enquiry-${q.id}`}>
                <div className="flex justify-between items-start gap-6">
                  <div>
                    <p className="text-hearth-ink font-semibold">{q.name || "No name"} · <span className="text-hearth-ochre">{q.contact}</span></p>
                    <p className="text-hearth-muted font-essay italic text-lg mt-2">"{q.message}"</p>
                  </div>
                  <button data-testid={`admin-enquiry-answer-${q.id}`} onClick={() => patch(`/admin/enquiries/${q.id}`, { answered: !q.answered }, "Enquiries")}
                    className={`shrink-0 inline-flex items-center gap-2 border px-4 py-2 text-xs uppercase tracking-widest transition-colors ${q.answered ? "border-hearth-ochre text-hearth-ochre" : "border-hearth-line text-hearth-muted hover:border-hearth-ember hover:text-hearth-ink"}`}>
                    <Check className="w-3 h-3" /> {q.answered ? "Answered" : "Mark answered"}
                  </button>
                </div>
              </div>
            ))}
            {(data.Enquiries || []).length === 0 && <p className="text-hearth-muted font-essay italic text-xl">The inbox is quiet.</p>}
          </div>
        )}

        {!loading && tab === "Words" && (
          <div className="space-y-4" data-testid="admin-testimonials">
            {(data.Words || []).map((t) => (
              <div key={t.id} className={`border p-6 ${t.featured ? "border-hearth-ochre bg-hearth-surface" : "border-hearth-line bg-hearth-surface"}`} data-testid={`admin-testimonial-${t.id}`}>
                <p className="text-hearth-ink font-essay text-lg">"{t.text}"</p>
                <div className="flex items-center justify-between mt-4">
                  <p className="text-hearth-muted text-xs uppercase tracking-widest">{t.tag || "anonymous"} · {t.approved ? "on the wall" : "hidden"}</p>
                  <div className="flex gap-2">
                    <button data-testid={`admin-testimonial-feature-${t.id}`} onClick={() => patch(`/admin/testimonials/${t.id}`, { featured: !t.featured }, "Words")}
                      className={`inline-flex items-center gap-2 border px-3 py-2 text-xs uppercase tracking-widest transition-colors ${t.featured ? "border-hearth-ochre text-hearth-ochre" : "border-hearth-line text-hearth-muted hover:border-hearth-ochre hover:text-hearth-ochre"}`}>
                      <Star className="w-3 h-3" /> {t.featured ? "Featured" : "Feature"}
                    </button>
                    <button data-testid={`admin-testimonial-hide-${t.id}`} onClick={() => patch(`/admin/testimonials/${t.id}`, { approved: !t.approved }, "Words")}
                      className="inline-flex items-center gap-2 border border-hearth-line text-hearth-muted px-3 py-2 text-xs uppercase tracking-widest hover:border-hearth-ember hover:text-hearth-ink transition-colors">
                      <EyeOff className="w-3 h-3" /> {t.approved ? "Hide" : "Show"}
                    </button>
                    <button data-testid={`admin-testimonial-delete-${t.id}`} onClick={() => del(`/admin/testimonials/${t.id}`, "Words")}
                      className="inline-flex items-center gap-2 border border-hearth-line text-hearth-muted px-3 py-2 text-xs uppercase tracking-widest hover:border-hearth-ember hover:text-hearth-ember transition-colors">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {(data.Words || []).length === 0 && <p className="text-hearth-muted font-essay italic text-xl">The wall is bare.</p>}
          </div>
        )}

        {!loading && tab === "Partners" && (
          <div className="space-y-4" data-testid="admin-partners">
            {(data.Partners || []).map((p) => (
              <div key={p.id} className="border border-hearth-line bg-hearth-surface p-6" data-testid={`admin-partner-${p.id}`}>
                <p className="text-hearth-ink font-semibold">{p.business_name} · <span className="text-hearth-ochre">{p.contact}</span></p>
                <p className="text-hearth-muted mt-2 text-sm">{p.offering}</p>
              </div>
            ))}
            {(data.Partners || []).length === 0 && <p className="text-hearth-muted font-essay italic text-xl">No hands up yet.</p>}
          </div>
        )}

        {!loading && tab === "Gift Codes" && (
          <div className="space-y-4" data-testid="admin-gifts">
            {(data["Gift Codes"] || []).map((g) => (
              <div key={g.id} className="border border-hearth-line bg-hearth-surface p-6 grid md:grid-cols-4 gap-4 text-sm" data-testid={`admin-gift-${g.id}`}>
                <p className="font-display font-bold text-hearth-ochre text-lg">{g.code}</p>
                <p className="text-hearth-muted">{g.session_type.replace(/_/g, " ")}</p>
                <p className="text-hearth-muted">Bought by {g.buyer_email}</p>
                <p className={g.status === "paid" ? "text-hearth-ochre" : "text-hearth-muted"}>{g.status}{g.recipient_note ? ` · "${g.recipient_note}"` : ""}</p>
              </div>
            ))}
            {(data["Gift Codes"] || []).length === 0 && <p className="text-hearth-muted font-essay italic text-xl">No gifts bought yet.</p>}
          </div>
        )}

        {!loading && tab === "Merch" && (
          <div className="space-y-4" data-testid="admin-merch-orders">
            {(data.Merch || []).map((o) => (
              <div key={o.id} className="border border-hearth-line bg-hearth-surface p-6" data-testid={`admin-merch-${o.id}`}>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    {o.items.map((i) => (
                      <p key={i.sku} className="text-hearth-ink">{i.quantity} × {i.name}{i.size ? ` (${i.size})` : ""}</p>
                    ))}
                    <p className="text-hearth-ochre mt-2 font-semibold">${(o.total_cents / 100).toFixed(0)} AUD</p>
                  </div>
                  <div>
                    <p className="text-hearth-muted">{o.recipient ? `${o.recipient.name}, ${o.recipient.city} ${o.recipient.state_code} ${o.recipient.zip}` : "Address lands with payment"}</p>
                    <p className="text-hearth-muted">{o.recipient?.email}</p>
                  </div>
                  <div>
                    <p className={o.payment_status === "paid" ? "text-hearth-ochre" : "text-hearth-muted"}>{o.payment_status}</p>
                    <p className="text-hearth-muted">{o.fulfilment_status.replace(/_/g, " ")}</p>
                  </div>
                </div>
              </div>
            ))}
            {(data.Merch || []).length === 0 && <p className="text-hearth-muted font-essay italic text-xl">No merch orders yet.</p>}
          </div>
        )}

        {!loading && tab === "The List" && (
          <div className="space-y-2" data-testid="admin-subscribers">
            {(data["The List"] || []).map((s) => (
              <p key={s.id} className="border border-hearth-line bg-hearth-surface px-6 py-4 text-sm text-hearth-ink" data-testid={`admin-subscriber-${s.id}`}>{s.email}</p>
            ))}
            {(data["The List"] || []).length === 0 && <p className="text-hearth-muted font-essay italic text-xl">The list is empty.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
